/**
 * MobDropIcons — Hollow Coil mob drop icons (Coil Art Pack, Lane C).
 *
 * Design source of truth: docs/design/coil-art/ICON_DESIGN_SHEETS.md
 * Palette: docs/design/coil-art/COLOR_PALETTE.md — do not invent colors.
 *
 * Conventions (per the style guide):
 * - 40×40 viewBox, content fills ~85% with a ~3px margin
 * - Bold silhouette + 2-3 colors max (body + accent + highlight)
 * - Gradients via <defs> (not flat fills), NO drop shadows (icons are flat UI)
 * - Thin strokes (1-2px) allowed for detail only
 * - Gradient ids are icon-prefixed (`ci_*`) so multiple inline icons on one
 *   page never collide in the DOM.
 *
 * Tier story: pig_foot (T1 bright warm) → boar_tusk/bee_stinger/bat_wing/
 * eye_shard (T2 desaturated cold) → dark_wood/hollow_essence (T3 near-black
 * sickly green). The set tells the same cozy→spooky story as the mob set.
 */

function wrap40(size: number, content: string): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">${content}</svg>`;
}

/** Tier 1 — a pink trotter: rounded, cartoonish, friendly. */
function pigFoot(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_pf_leg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#ffb6c1"/>
        <stop offset="100%" stop-color="#ff9fac"/>
      </linearGradient>
    </defs>
    <!-- Leg (rounded oval, flat toward the hoof) -->
    <path d="M11,10 Q11,5 20,5 Q29,5 29,10 L29,25 Q29,27 26,27 L14,27 Q11,27 11,25 Z" fill="url(#ci_pf_leg)"/>
    <!-- Toe dots -->
    <ellipse cx="16" cy="21.5" rx="1.8" ry="2.2" fill="#d4a0a8" stroke="rgba(0,0,0,0.15)" stroke-width="0.8"/>
    <ellipse cx="24" cy="21.5" rx="1.8" ry="2.2" fill="#d4a0a8" stroke="rgba(0,0,0,0.15)" stroke-width="0.8"/>
    <!-- Cloven hoof (flat bottom, center cleft) -->
    <path d="M11,24 L29,24 L29,31 Q29,35 25,35 L22.5,35 L22.5,29.5 L17.5,29.5 L17.5,35 L15,35 Q11,35 11,31 Z" fill="#d4a0a8"/>
    <path d="M11,24 L29,24" stroke="rgba(0,0,0,0.15)" stroke-width="1"/>
    <!-- Warm highlight, top-left -->
    <ellipse cx="15.5" cy="9.5" rx="4" ry="2.2" fill="rgba(255,255,255,0.4)" transform="rotate(-15 15.5 9.5)"/>
  `);
}

/** Tier 2 — a curved ivory tusk, sharp point, brown gum root. */
function boarTusk(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_bt_tusk" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stop-color="#d4c890"/>
        <stop offset="100%" stop-color="#f5f5dc"/>
      </linearGradient>
    </defs>
    <!-- Tusk crescent: thick base bottom-left, sharp tip top-right -->
    <path d="M9,30 Q10,25 15,21 Q23,15 33,6 Q31,31 13,35.5 Q9,35 9,30 Z" fill="url(#ci_bt_tusk)"/>
    <!-- Sharp tip catches light -->
    <path d="M33,6 L28.5,13.5 L30.5,14.5 Z" fill="#ffffff" opacity="0.9"/>
    <!-- Gum root at the base -->
    <path d="M8,27 Q13,25 17,26 Q18,30 16,34 Q12,37 9,34 Q7,31 8,27 Z" fill="#8b4513"/>
  `);
}

/** Tier 2 — a thin barbed stinger, black spike + gold bee-body base. */
function beeStinger(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_bs_sting" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#2a2a2a"/>
        <stop offset="100%" stop-color="#1a1a1a"/>
      </linearGradient>
    </defs>
    <!-- Gold bee-body base -->
    <ellipse cx="20" cy="9" rx="8" ry="6" fill="#ffcc00"/>
    <path d="M13,10.5 Q20,14 27,10.5" stroke="#1a1a1a" stroke-width="2.5" fill="none"/>
    <!-- Stinger spike -->
    <polygon points="14,13 26,13 20,37" fill="url(#ci_bs_sting)"/>
    <!-- Barbs -->
    <polygon points="16.9,21 13,19 16.2,24" fill="#1a1a1a"/>
    <polygon points="23.4,20 27,18 24,23" fill="#1a1a1a"/>
    <polygon points="18,28 15.5,26.5 17.6,30.5" fill="#1a1a1a"/>
    <polygon points="22.2,27 24.6,25.5 22.7,29.5" fill="#1a1a1a"/>
    <!-- Flat overcast glint (Tier 2 — no warm highlight) -->
    <path d="M18,14.5 L19.6,30" stroke="#aab3a6" stroke-width="1" opacity="0.3" stroke-linecap="round" fill="none"/>
  `);
}

/** Tier 2 — a dark tattered bat wing, indigo membrane with tears. */
function batWing(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <radialGradient id="ci_bw_mem" cx="20%" cy="15%" r="95%">
        <stop offset="0%" stop-color="#4b0082"/>
        <stop offset="100%" stop-color="#2a004a"/>
      </radialGradient>
    </defs>
    <!-- Membrane (scalloped trailing edge; two tears cut out via evenodd) -->
    <path fill-rule="evenodd" fill="url(#ci_bw_mem)" opacity="0.85"
      d="M7,7 Q22,2 35,9 Q28,14 33,23 Q24,25 23,34 Q15,27 10,24 Q7,16 7,7 Z
         M26,17 L28.5,21 L25,20.5 Z
         M17,24 L19.5,28 L15.8,27 Z"/>
    <!-- Arm + fingers (thin dark bones) -->
    <path d="M7,7 Q10,8 13,9" stroke="#2a004a" stroke-width="2.5" fill="none" stroke-linecap="round"/>
    <path d="M13,9 L35,9 M13,9 L33,23 M13,9 L23,34 M13,9 L15,4.5" stroke="#2a004a" stroke-width="1.6" fill="none" stroke-linecap="round"/>
  `);
}

/** Tier 2 — a crystalline purple eye fragment, faint corruption glow. */
function eyeShard(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_es_shard" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#9400d3"/>
        <stop offset="100%" stop-color="#6a00a0"/>
      </linearGradient>
      <radialGradient id="ci_es_glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#00ff7f" stop-opacity="0.2"/>
        <stop offset="100%" stop-color="#00ff7f" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <!-- Irregular shard -->
    <polygon points="20,3.5 30.5,11 28,26 18,36 10.5,19" fill="url(#ci_es_shard)"/>
    <!-- Facet lines -->
    <path d="M20,3.5 L19,20 L18,36 M10.5,19 L19,20 L28,26" stroke="#6a00a0" stroke-width="0.8" fill="none"/>
    <!-- Faint inner corruption glow -->
    <circle cx="19.5" cy="19" r="9" fill="url(#ci_es_glow)"/>
    <!-- Blood vein -->
    <path d="M15,9 Q21,17 17.5,29" stroke="#cc3333" stroke-width="1.1" opacity="0.6" fill="none"/>
    <!-- Edge glint -->
    <path d="M20,3.5 L11.5,17" stroke="rgba(255,255,255,0.35)" stroke-width="1" fill="none"/>
  `);
}

/** Tier 3 — a twisted chunk of corrupted wood, glowing green crack. */
function darkWood(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <linearGradient id="ci_dw_wood" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#2f1b14"/>
        <stop offset="100%" stop-color="#1a0d08"/>
      </linearGradient>
    </defs>
    <!-- Jagged splintered chunk -->
    <path d="M8,14 L15,6 L24,8.5 L33,7 L31,15 L34,22 L29,32 L18,30 L14,35 L9,27 L6,20 Z" fill="url(#ci_dw_wood)"/>
    <!-- Twisted grain -->
    <path d="M11,16 Q18,14 27,12 M11,22 Q19,20 29,19 M12,27 Q18,25.5 26,25" stroke="#3a2418" stroke-width="1" fill="none"/>
    <!-- Glowing corruption crack (soft glow under a sharp line) -->
    <path d="M19,7 L22,13 L17.5,19 L23,26 L20,32" stroke="#00ff7f" stroke-width="3.6" opacity="0.18" fill="none" stroke-linejoin="round"/>
    <path d="M19,7 L22,13 L17.5,19 L23,26 L20,32" stroke="#00ff7f" stroke-width="1.4" opacity="0.6" fill="none" stroke-linejoin="round"/>
  `);
}

/**
 * Tier 3 BOSS DROP — the Hollow Keeper's remains. A swirling spectral
 * vortex: wispy green-grey edges, spiral interior (the Coil), a bright
 * ambiguous center — corruption departing, or the keeper's soul persisting?
 */
function hollowEssence(size: number = 32): string {
  return wrap40(size, `
    <defs>
      <radialGradient id="ci_he_outer" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#4a5a55" stop-opacity="0.25"/>
        <stop offset="55%" stop-color="#4a5a55" stop-opacity="0.4"/>
        <stop offset="100%" stop-color="#4a5a55" stop-opacity="0"/>
      </radialGradient>
      <radialGradient id="ci_he_core" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stop-color="#ffffff" stop-opacity="0.9"/>
        <stop offset="35%" stop-color="#00ff7f" stop-opacity="0.8"/>
        <stop offset="100%" stop-color="#00ff7f" stop-opacity="0"/>
      </radialGradient>
    </defs>
    <!-- Outer wisp (fades to nothing at the edges) -->
    <circle cx="20" cy="20" r="17" fill="url(#ci_he_outer)"/>
    <!-- Faint outer wisps -->
    <path d="M24,6 Q31,10 31,17 M16,34 Q9,30 9,23" stroke="#4a5a55" stroke-width="1.6" opacity="0.7" fill="none" stroke-linecap="round"/>
    <!-- Mid swirl arms (the spiral of the Coil) — dark core under a lighter
         fogbound edge so the vortex still reads on dark inventory slots -->
    <path d="M20,4.5 Q34,7 33.5,21 Q33,30 25,32" stroke="#2f3b38" stroke-width="4.4" opacity="0.9" fill="none" stroke-linecap="round"/>
    <path d="M20,35.5 Q6,33 6.5,19 Q7,10 15,8" stroke="#2f3b38" stroke-width="4.4" opacity="0.9" fill="none" stroke-linecap="round"/>
    <path d="M20,4.5 Q34,7 33.5,21 Q33,30 25,32" stroke="#4a5a55" stroke-width="2" opacity="0.85" fill="none" stroke-linecap="round"/>
    <path d="M20,35.5 Q6,33 6.5,19 Q7,10 15,8" stroke="#4a5a55" stroke-width="2" opacity="0.85" fill="none" stroke-linecap="round"/>
    <!-- Corruption tinge bleeding along the arms toward the center -->
    <path d="M33.5,21 Q33,30 25,32 M6.5,19 Q7,10 15,8" stroke="#00ff7f" stroke-width="1.2" opacity="0.25" fill="none" stroke-linecap="round"/>
    <!-- Inner spiral pulling into the center -->
    <path d="M20,11 Q28,13 27.5,20.5 Q27,26.5 21,26.5 Q16.5,26.5 16.5,22.5 Q16.5,19 20,19" stroke="#00ff7f" stroke-width="2" opacity="0.45" fill="none" stroke-linecap="round"/>
    <!-- Inner glow + the keeper's last spark -->
    <circle cx="20" cy="20" r="7.5" fill="url(#ci_he_core)"/>
    <circle cx="20" cy="20" r="1.4" fill="#ffffff" opacity="0.6"/>
  `);
}

export const MOB_DROP_ICONS: Record<string, (size?: number) => string> = {
  pig_foot: pigFoot,
  boar_tusk: boarTusk,
  bee_stinger: beeStinger,
  bat_wing: batWing,
  eye_shard: eyeShard,
  dark_wood: darkWood,
  hollow_essence: hollowEssence,
};
